// src/components/Tax Pro Only/ClientStatus/ClientStatus.tsx
import { Combobox, ScrollArea, Tooltip } from "@mantine/core";
import type { FC } from "react";
import React, { useEffect, useState, useMemo, memo } from "react";
import "./ClientStatus.scss";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { clientStatuses } from "../../../constants";
import { StatusBadge } from "../../common/StatusBadge";
import { useClientStatus } from "../../../utils/hooks/useClientStatus"; // Updated import

interface ClientStatusProps {
  status: string;
  userId: string;
  taxYear?: number;
}

/**
 * Renders a client status component with interactive functionalities such as updating client status.
 *
 * @param {ClientStatusProps} props - The component props.
 * @return {JSX.Element} The client status component.
 */

const ClientStatus: FC<ClientStatusProps> = memo(
  ({ status, userId, taxYear }) => {
    const combobox = useClientStatus(userId, status, taxYear); // Pass userId, status, and taxYear
    const [value, setValue] = useState<string>("");

    /**
     * Maps the current status to its corresponding class and sets the initial value.
     */

    useEffect(() => {
      const currentStatus =
        clientStatuses[status as keyof typeof clientStatuses]?.status;
      setValue(currentStatus || "Unknown");
    }, [status]);

    /**
     * Memoize status options to prevent re-creation on every render.
     */
    const shouldFilterOptions = false;

    const filteredOptions = useMemo(() => {
      // Filter out Archived status from the dropdown
      const allStatuses = Object.values(clientStatuses).filter(
        (item) => item.status !== "Archived"
      );

      return shouldFilterOptions
        ? allStatuses.filter((item) =>
            item.status.toLowerCase().includes(value.toLowerCase().trim())
          )
        : allStatuses;
    }, [shouldFilterOptions, value]);

    const options = useMemo(() => {
      return filteredOptions.map((item) => {
        return (
          <Combobox.Option value={item.status} key={item.status}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <StatusBadge status={item.status} />
              {item.status === value && (
                <IconCheck className="icon" style={{ flexShrink: 0 }} />
              )}
            </div>
          </Combobox.Option>
        );
      });
    }, [filteredOptions, value]);

    /**
     * Prevents the Combobox from triggering parent onClick events when interacting with it.
     */
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.stopPropagation();
      }
    };

    return (
      <div
        className="clientStatus-container"
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <Combobox
          position="bottom"
          middlewares={{ flip: true, shift: false }}
          onOptionSubmit={(optionValue) => {
            setValue(optionValue);
            combobox.handleStatusUpdate(optionValue);
            combobox.combobox.closeDropdown();
          }}
          store={combobox.combobox}
        >
          <Combobox.Target>
            <Tooltip label={value ?? "Select Status"} withArrow>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                }}
                data-testid="client-status-button"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent parent onClick
                  combobox.combobox.toggleDropdown();
                }}
              >
                <StatusBadge status={value} />
                <IconChevronDown className="clientStatus-icon" size={16} />
              </div>
            </Tooltip>
          </Combobox.Target>

          <Combobox.Dropdown
            onClick={(e) => e.stopPropagation()} // Prevent parent onClick when clicking in dropdown
          >
            <Combobox.Options>
              <ScrollArea.Autosize
                type="always"
                scrollbarSize={8}
                offsetScrollbars
                mah={400}
              >
                {options}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </div>
    );
  }
);

ClientStatus.displayName = "ClientStatus";

export default ClientStatus;
